<template>
  <b-card
    v-if="shouldRender"
    :title="$t('order.custom-fields.title')"
    class="card-justify"
  >
    <b-card-text>
      <rc-overlay :show="loading">
        <b-row>
          <b-col
            v-for="definition in renderedDefinitions"
            :key="definition.key"
            cols="12"
            class="pb-1"
          >
            <label
              v-if="definition.type !== 'checkbox' || readonly"
              :for="`cf-${definition.key}`"
            >{{ definition.label }}</label>

            <template v-if="readonly">
              <p class="mb-0">
                {{ getDisplayValue(definition) }}
              </p>
            </template>

            <validation-provider
              v-else
              #default="{ errors }"
              :name="definition.label"
              :rules="definition.validation"
            >
              <b-form-input
                v-if="definition.type === 'text'"
                :id="`cf-${definition.key}`"
                :value="getValue(definition.key)"
                @change="val => handleChange(definition.key, val)"
              />

              <v-select
                v-else-if="definition.type === 'select'"
                :id="`cf-${definition.key}`"
                :value="getValue(definition.key)"
                :options="definition.options"
                :reduce="opt => opt.value"
                label="label"
                :clearable="true"
                @input="val => handleChange(definition.key, val)"
              />

              <v-select
                v-else-if="definition.type === 'multiselect'"
                :id="`cf-${definition.key}`"
                :value="getMultiselectValue(definition.key)"
                :options="definition.options"
                :reduce="opt => opt.value"
                label="label"
                :multiple="true"
                :clearable="true"
                @input="val => handleChange(definition.key, val)"
              />

              <b-form-checkbox
                v-else-if="definition.type === 'checkbox'"
                :id="`cf-${definition.key}`"
                :checked="!!getValue(definition.key)"
                @change="val => handleChange(definition.key, val)"
              >
                {{ definition.label }}
              </b-form-checkbox>

              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-col>
        </b-row>
      </rc-overlay>
    </b-card-text>
  </b-card>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'

// Stable reference returned when a multiselect field has no array value yet (null/undefined/legacy string).
// VeeValidate's ValidationProvider mutates its own `value` during render; a new [] each render
// would cause an infinite render loop because [] !== [] by reference.
const EMPTY_MULTISELECT = []

export default {
  name: 'OrderCustomFields',
  components: {
    ValidationProvider,
    vSelect,
    RcOverlay,
  },
  props: {
    definitions: {
      type: Array,
      default: () => [],
    },
    values: {
      type: Object,
      default: () => ({}),
    },
    orderType: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    visibleDefinitions() {
      return this.definitions.filter(def => {
        if (!def.orderTypes || def.orderTypes.length === 0) {
          return true
        }
        return def.orderTypes.includes(this.orderType)
      })
    },
    renderedDefinitions() {
      if (!this.readonly) return this.visibleDefinitions
      return this.visibleDefinitions.filter(def => this.hasValue(def))
    },
    hasAnyVisibleValue() {
      return this.visibleDefinitions.some(def => this.hasValue(def))
    },
    shouldRender() {
      if (this.visibleDefinitions.length === 0) return false
      if (this.readonly) return this.hasAnyVisibleValue
      return true
    },
  },
  methods: {
    hasValue(definition) {
      const val = this.values[definition.key]
      if (val === null || val === undefined) return false
      if (definition.type === 'multiselect') return Array.isArray(val) && val.length > 0
      if (definition.type === 'checkbox') return true
      return val !== ''
    },
    getValue(key) {
      return this.values[key] !== undefined ? this.values[key] : ''
    },
    getMultiselectValue(key) {
      const val = this.values[key]
      return Array.isArray(val) ? val : EMPTY_MULTISELECT
    },
    getDisplayValue(definition) {
      const val = this.getValue(definition.key)
      if (definition.type === 'checkbox') {
        return val ? 'Tak' : 'Nie'
      }
      if (definition.type === 'select') {
        const opt = (definition.options || []).find(o => o.value === val)
        return opt ? opt.label : val
      }
      if (definition.type === 'multiselect') {
        return this.getMultiselectValue(definition.key)
          .map(v => {
            const opt = (definition.options || []).find(o => o.value === v)
            return opt ? opt.label : v
          })
          .join(', ')
      }
      return val
    },
    handleChange(key, value) {
      this.$emit('update:values', { ...this.values, [key]: value })
    },
  },
}
</script>
